import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Trash2, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminBancos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBanco, setNewBanco] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchBancos();
  }, []);

  const fetchBancos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bancos`);
      setBancos(response.data);
    } catch (error) {
      console.error('Error al cargar bancos:', error);
      toast.error('Error al cargar bancos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBanco = async (e) => {
    e.preventDefault();
    
    if (!newBanco.trim()) {
      toast.error('Por favor ingresa el nombre del banco');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/admin/bancos`, {
        nombre: newBanco.trim()
      });
      
      toast.success('Banco agregado exitosamente');
      setNewBanco('');
      fetchBancos();
    } catch (error) {
      console.error('Error al agregar banco:', error);
      toast.error(error.response?.data?.detail || 'Error al agregar banco');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteBanco = async (bancoId, nombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar el banco "${nombre}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/bancos/${bancoId}`);
      toast.success('Banco eliminado exitosamente');
      fetchBancos();
    } catch (error) {
      console.error('Error al eliminar banco:', error);
      toast.error('Error al eliminar el banco');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Dashboard
          </Button>
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-blue-100 rounded-full p-3">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Gestión de Bancos</h1>
              <p className="text-slate-600">Administra la lista de bancos disponibles</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Add Banco Form */}
          <Card className="p-6 h-fit">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Agregar Banco</h2>
            <form onSubmit={handleAddBanco} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre del Banco *</Label>
                <Input
                  id="nombre"
                  value={newBanco}
                  onChange={(e) => setNewBanco(e.target.value)}
                  placeholder="Ej: Banco Industrial"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={adding}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="mr-2 h-4 w-4" />
                {adding ? 'Agregando...' : 'Agregar Banco'}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                <strong>Nota:</strong> Los bancos agregados estarán disponibles en el formulario de registro de depósitos.
              </p>
            </div>
          </Card>

          {/* Bancos List */}
          <Card className="p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900">
                Bancos Registrados ({bancos.length})
              </h2>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <p className="text-slate-600">Cargando bancos...</p>
              </div>
            ) : bancos.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">No hay bancos registrados</p>
                <p className="text-sm text-slate-500 mt-2">Agrega el primer banco usando el formulario</p>
              </div>
            ) : (
              <div className="space-y-2">
                {bancos.map((banco) => (
                  <div
                    key={banco.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-slate-900">{banco.nombre}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBanco(banco.id, banco.nombre)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
